import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PHONE_COUNTRIES } from './phone-codes';

// Compact country dial-code picker used by <Input variant="phone">.
// A small trigger (ISO + dial) inside the input row opens a portalled,
// searchable panel — the portal lets it escape the input row's overflow-hidden.

interface Props {
  value: string; // selected dial, e.g. '+91'
  onChange: (dial: string) => void;
  disabled?: boolean;
}

export function PhoneCountrySelect({ value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const selected = PHONE_COUNTRIES.find((c) => c.dial === value);

  const updatePos = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: rect.left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? PHONE_COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dial.includes(q) ||
          c.iso.toLowerCase().includes(q),
      )
    : PHONE_COUNTRIES;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country code: ${selected?.name ?? value}`}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 h-full pl-3 pr-2 border-r border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/40 text-neutral-700 dark:text-neutral-200 text-sm select-none shrink-0 outline-none hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-70 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
      >
        <span className="font-medium tabular-nums">{selected?.dial ?? value}</span>
        <svg className="h-3 w-3 text-neutral-400 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            role="listbox"
            style={{ position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0 }}
            className="z-[9999] w-72 max-w-[calc(100vw-1rem)] rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 shadow-lg overflow-hidden animate-fade-in"
          >
            <div className="p-2 border-b border-neutral-100 dark:border-neutral-700">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or code…"
                className="w-full h-9 px-3 rounded-md bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-600 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <ul className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <li className="px-3 py-3 text-sm text-neutral-400 text-center">No matches</li>
              )}
              {filtered.map((c) => {
                const isSel = c.dial === value;
                return (
                  <li key={c.iso}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSel}
                      onClick={() => {
                        onChange(c.dial);
                        setOpen(false);
                      }}
                      className={[
                        'flex w-full items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                        isSel
                          ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                          : 'text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700',
                      ].join(' ')}
                    >
                      <span className="inline-flex items-center justify-center w-7 shrink-0 text-[10px] font-semibold text-neutral-500 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-600 rounded px-1 py-0.5">
                        {c.iso}
                      </span>
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className="text-neutral-400 dark:text-neutral-500 tabular-nums shrink-0">{c.dial}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}
